import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { planningSettingsQuery, rebuildPlanningCron, savePlanningSettings } from '../../api/settingsEaseedeskPlanning'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { SettingsCheck } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Easeedesk -> Planning, element cu element fata de
 * `easeedesk_settings/planning/index.php` + `general.php`: butonul
 * "Refresh cron" sus, apoi cele trei randuri - tipurile cu bifa de planning,
 * Stop SLA, culoarea statusului (colpick -> `<input type="color">`, aceeasi
 * valoare `#rrggbb`) - si "Update settings". Textele sunt ale machetei.
 */
const ROW: React.CSSProperties = {
  padding: '10px 20px',
  marginBottom: 4,
  borderRadius: 4,
  borderWidth: '1px 6px',
  borderColor: '#ddd',
  borderStyle: 'solid',
}

/**
 * `<input type="color">` primeste NUMAI `#rrggbb` cu litere mici; orice
 * altceva - gol, `DDDDDD` fara diez, trei cifre - ii arata NEGRU, si negru se
 * salveaza peste culoarea de dinainte la prima apasare pe Update settings.
 * Capul de api are aceeasi plasa (`EaseedeskPlanning::DEFAULT_COLOR`), dar
 * intoarce ce e in baza asa cum e scris acolo.
 */
function hex(value: string): string {
  const one = String(value ?? '').trim()
  if (/^#[0-9a-fA-F]{6}$/.test(one)) return one.toLowerCase()
  if (/^#[0-9a-fA-F]{3}$/.test(one)) return `#${one[1]}${one[1]}${one[2]}${one[2]}${one[3]}${one[3]}`.toLowerCase()
  return '#dddddd'
}

export function PlanningSettingsPage() {
  const client = useQueryClient()
  const page = useQuery(planningSettingsQuery())
  const [checked, setChecked] = useState<Record<number, boolean>>({})
  const [stopSla, setStopSla] = useState(false)
  const [color, setColor] = useState('#DDDDDD')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!page.data) return
    const next: Record<number, boolean> = {}
    for (const type of page.data.ticket_types) next[type.ID] = type.checked === 1
    setChecked(next)
    setStopSla(page.data.settings.stop_sla === 1)
    setColor(hex(page.data.settings.planning_color))
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <LoadFailed what="Planning settings" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  const rebuild = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void rebuildPlanningCron()
      .then((answer) => {
        setBusy(false)
        if (answer.result === 'done') toast('Configuration build successfully.', 'Success')
        else toast(answer.message || 'Something went wrong.', 'Error', 'error')
      })
      .catch(() => {
        setBusy(false)
        toast('Something went wrong.', 'Error', 'error')
      })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void savePlanningSettings(checked, color, stopSla)
      .then(() => {
        setBusy(false)
        toast('Settings changed.', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'planning'] })
      })
      .catch(() => {
        setBusy(false)
        toast('Something went wrong.', 'Error', 'error')
      })
  }

  return (
    <div>
      <h4>
        <strong>Planning</strong> settings
      </h4>
      <hr />

      <section className="settings">
        <div className="row">
          <div className="col-md-12">
            <form id="rebuildConfigurationForm" onSubmit={rebuild}>
              <div className="form-group row">
                <div className="col-md-12">
                  <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
                    <i className="fas fa-sync-alt" aria-hidden="true"></i> Refresh cron
                  </button>
                  <span className="help-block">Refresh planning cron after every change.</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ width: 300 }}>General settings</h5>
          </div>
          <div className="col-md-10">
            <div className="general-group">
              <form className="change_planning_settings" onSubmit={submit}>
                <ul>
                  <li style={ROW}>
                    <div className="row">
                      <div className="col-md-4">
                        <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                          Activate planning
                        </h5>
                      </div>
                      <div className="col-md-8">
                        {page.data.ticket_types.length > 0 ? (
                          page.data.ticket_types.map((type) => (
                            <div key={type.ID} style={{ display: 'contents' }}>
                              <div className="col-md-2" style={{ padding: '0 0 5px 0' }}>
                                {/*
                                 * `od_console.css` da fiecarui `.badge` cerneala inchisa
                                 * (`--od-ink-soft`, #5b6672) si macheta scrie doar fundalul, deci
                                 * prescurtarea se citea inchis pe culoarea tipului. In portal era
                                 * alba, din `.badge` al lui Bootstrap.
                                 */}
                                <span className="badge"
                                  style={{ minWidth: 23, backgroundColor: type.color_identifier, color: '#fff', textAlign: 'center' }}>
                                  {type.short_name}
                                </span>
                              </div>
                              <div className="col-md-6" style={{ padding: '0 0 5px 0' }}>
                                {type.type_name}
                              </div>
                              <div className="col-md-4" style={{ padding: '0 0 5px 0' }}>
                                <div className="pull-right">
                                  {/*
                                   * Clasa era carligul lui Switchery, plugin pe
                                   * care v4 nu-l incarca: nicio regula in foile
                                   * servite, deci o casuta goala de 15px
                                   * plutita la dreapta, fara nimic langa ea
                                   * care sa spuna ce comuta. Acum e bifa zonei,
                                   * cu cuvantul ei; `name` ramane cel din
                                   * formularul vechi.
                                   */}
                                  <SettingsCheck
                                    label="Planning"
                                    name={`ticket_types[${type.ID}][status]`}
                                    checked={checked[type.ID] === true}
                                    onChange={(on) => setChecked({ ...checked, [type.ID]: on })}
                                  />
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="alert alert-warning" style={{ margin: 0 }}>
                            <i className="fas fa-info-circle" aria-hidden="true"></i> No active ticket types found. You
                            must activate the ticket types first.
                          </div>
                        )}
                      </div>
                    </div>
                  </li>

                  <li style={ROW}>
                    <div className="row">
                      <div className="col-md-4">
                        <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                          Stop SLA
                        </h5>
                      </div>
                      <div className="col-md-1"></div>
                      <div className="col-md-5"></div>
                      <div className="col-md-2">
                        <div className="pull-right">
                          <SettingsCheck label="Active" name="settings[stop_sla]" checked={stopSla} onChange={setStopSla} />
                        </div>
                      </div>
                    </div>
                  </li>

                  <li style={ROW}>
                    <div className="row">
                      <div className="col-md-4">
                        <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                          Status color
                        </h5>
                      </div>
                      <div className="col-md-1">
                        <span className="badge" style={{ minWidth: 23, minHeight: 23, backgroundColor: hex(color) }}>
                          {' '}
                        </span>
                      </div>
                      <div className="col-md-5">
                        <input
                          type="color"
                          className="form-control planning-color"
                          name="settings[color]"
                          aria-label="Planning status colour"
                          value={hex(color)}
                          onChange={(e) => setColor(e.target.value)}
                        />
                      </div>
                      <div className="col-md-2"></div>
                    </div>
                  </li>
                </ul>

                <div className="row" style={{ marginTop: 20 }}>
                  <div className="col-md-12">
                    <button className="btn btn-primary pull-right update-planning-settings" type="submit" disabled={busy}>
                      Update settings
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <hr className="dotted" />
      </section>
    </div>
  )
}
